// Centralized WSR Upload Service
// This service handles file uploads from the top navbar and processes WSR files

interface WSRAnalysisResult {
  weekEnding: string;
  reportingPeriod: string;
  reportingDate: string;
  projectSponsor: string;
  preparedBy: string;
  phone: string;
  email: string;
  reportSummary: string;
  workAccomplished: Array<{
    task: string;
    notes?: string;
    status: 'completed';
  }>;
  nextWeekTargets: Array<{
    task: string;
    notes?: string;
    priority: 'high' | 'medium' | 'low';
  }>;
  risks: Array<{
    risk: string;
    notes?: string;
    priority: 'high' | 'medium' | 'low';
  }>;
  metrics: Array<{
    metric_name: string;
    current_value: string;
    target_value?: string;
    status: 'on-track' | 'at-risk' | 'behind';
    trend?: 'improving' | 'stable' | 'declining';
  }>;
  conclusionsRecommendations: string;
  originalFileName: string;
  originalFileType: string;
  originalFileSize: number;
}

class WSRUploadService {
  private static instance: WSRUploadService;
  private analysisCallbacks: ((analysis: WSRAnalysisResult) => void)[] = [];

  static getInstance(): WSRUploadService {
    if (!WSRUploadService.instance) {
      WSRUploadService.instance = new WSRUploadService();
    }
    return WSRUploadService.instance;
  }

  // Register callback for when analysis is complete
  onAnalysisComplete(callback: (analysis: WSRAnalysisResult) => void) {
    this.analysisCallbacks.push(callback);
  }

  // Remove callback
  removeAnalysisCallback(callback: (analysis: WSRAnalysisResult) => void) {
    this.analysisCallbacks = this.analysisCallbacks.filter(cb => cb !== callback);
  }

  // Handle file upload from navbar
  async handleFileUpload(file: File): Promise<WSRAnalysisResult> {
    if (!this.isValidWSRFile(file)) {
      throw new Error('Invalid file type. Please upload a .md, .txt, or .pdf WSR file.');
    }

    try {
      const content = await this.readFileContent(file);
      const analysis = this.parseWSRContent(content, file);
      
      // Notify all registered callbacks
      this.analysisCallbacks.forEach(callback => callback(analysis));
      
      return analysis;
    } catch (error) {
      throw new Error(`Failed to process WSR file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private isValidWSRFile(file: File): boolean {
    const validTypes = ['text/markdown', 'text/plain', 'application/pdf'];
    const validExtensions = ['.md', '.txt', '.pdf'];
    
    return validTypes.includes(file.type) || 
           validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  }

  private async readFileContent(file: File): Promise<string> {
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      return this.extractTextFromPDF(file);
    }
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  private async extractTextFromPDF(file: File): Promise<string> {
    try {
      // Use PDF.js to extract text from PDF
      const pdfjsLib = await import('pdfjs-dist');
      
      // Set worker source
      pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      
      // Extract text from all pages
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        
        fullText += pageText + '\n';
      }
      
      return fullText;
    } catch (error) {
      throw new Error(`Failed to extract text from PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private parseWSRContent(content: string, file: File): WSRAnalysisResult {
    // Extract header information
    const weekEndingMatch = content.match(/Week Ending.*?(\d{4}-\d{2}-\d{2}|\w+ \d{1,2}, \d{4})/i);
    const reportingPeriodMatch = content.match(/Reporting Period.*?([^\n\r|]+)/i);
    const reportingDateMatch = content.match(/Reporting Date.*?(\d{4}-\d{2}-\d{2}|\w+ \d{1,2}, \d{4})/i);
    const sponsorMatch = content.match(/Project Sponsor.*?([^\n\r|]+)/i);
    const preparedByMatch = content.match(/Prepared By.*?([^\n\r|]+)/i);
    const phoneMatch = content.match(/\((\d{3})\)\s*(\d{3})-(\d{4})/);
    const emailMatch = content.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);

    // Extract sections
    const workAccomplishedSection = content.match(/WORK ACCOMPLISHED[\s\S]*?(?=NEXT WEEK|TARGETS|RISKS|$)/i)?.[0] || '';
    const targetsSection = content.match(/(?:NEXT WEEK|TARGETS)[\s\S]*?(?=RISKS|CONCLUSIONS|$)/i)?.[0] || '';
    const risksSection = content.match(/RISKS[\s\S]*?(?=CONCLUSIONS|RECOMMENDATIONS|$)/i)?.[0] || '';
    const conclusionsSection = content.match(/CONCLUSIONS[\s\S]*$/i)?.[0] || '';

    // Parse work accomplished
    const workAccomplished = this.parseTableRows(workAccomplishedSection).map(row => ({
      task: row.task || row.item || '',
      notes: row.notes || row.assessment || '',
      status: 'completed' as const
    }));

    // Parse targets
    const nextWeekTargets = this.parseTableRows(targetsSection).map(row => ({
      task: row.task || row.tasks || '',
      notes: row.notes || '',
      priority: this.determinePriority(row.task || row.tasks || '') as 'high' | 'medium' | 'low'
    }));

    // Parse risks
    const risks = this.parseTableRows(risksSection).map(row => ({
      risk: row.risk || row.risks || '',
      notes: row.notes || '',
      priority: this.determinePriority(row.risk || row.risks || '') as 'high' | 'medium' | 'low'
    }));

    // Extract metrics
    const metrics = this.extractMetrics(content);

    return {
      weekEnding: weekEndingMatch?.[1] || '',
      reportingPeriod: reportingPeriodMatch?.[1]?.trim() || '',
      reportingDate: reportingDateMatch?.[1] || '',
      projectSponsor: sponsorMatch?.[1]?.trim() || '',
      preparedBy: preparedByMatch?.[1]?.trim() || '',
      phone: phoneMatch ? `(${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}` : '',
      email: emailMatch?.[1] || '',
      reportSummary: this.extractReportSummary(content),
      workAccomplished,
      nextWeekTargets,
      risks,
      metrics,
      conclusionsRecommendations: this.extractConclusions(conclusionsSection),
      originalFileName: file.name,
      originalFileType: file.type || this.getFileTypeFromExtension(file.name),
      originalFileSize: file.size
    };
  }

  private parseTableRows(section: string) {
    const rows: any[] = [];
    const tableMatch = section.match(/\|([^|]+)\|([^|]+)\|/g);
    
    if (tableMatch) {
      tableMatch.forEach(row => {
        const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
        if (cells.length >= 2) {
          rows.push({
            task: cells[0] || '',
            tasks: cells[0] || '',
            item: cells[0] || '',
            risk: cells[0] || '',
            risks: cells[0] || '',
            notes: cells[1] || '',
            assessment: cells[1] || ''
          });
        }
      });
    } else {
      // Fallback: parse bullet points or numbered lists
      const lines = section.split('\n').filter(line => 
        line.trim() && 
        (line.includes('•') || line.includes('-') || /^\d+\./.test(line.trim()))
      );
      
      lines.forEach(line => {
        const cleanLine = line.replace(/^[\s•\-\d\.]+/, '').trim();
        if (cleanLine) {
          rows.push({
            task: cleanLine,
            tasks: cleanLine,
            item: cleanLine,
            risk: cleanLine,
            risks: cleanLine,
            notes: ''
          });
        }
      });
    }
    
    return rows;
  }

  private determinePriority(text: string): string {
    const lowerText = text.toLowerCase();
    if (lowerText.includes('critical') || lowerText.includes('urgent') || lowerText.includes('high')) {
      return 'high';
    } else if (lowerText.includes('low') || lowerText.includes('minor')) {
      return 'low';
    }
    return 'medium';
  }

  private extractMetrics(content: string) {
    const metrics: any[] = [];
    
    // Look for common performance metrics patterns
    const metricPatterns = [
      /Database Response Time.*?(\d+(?:\.\d+)?)\s*(ms|seconds?)/i,
      /Query Success Rate.*?(\d+(?:\.\d+)?)\s*%/i,
      /CPU Utilization.*?(\d+(?:\.\d+)?)\s*%/i,
      /Memory Utilization.*?(\d+(?:\.\d+)?)\s*%/i,
      /Storage.*?(\d+(?:\.\d+)?)\s*%/i,
      /Index Fragmentation.*?(\d+(?:\.\d+)?)\s*%/i
    ];

    metricPatterns.forEach(pattern => {
      const match = content.match(pattern);
      if (match) {
        const metricName = pattern.source.split('.*?')[0].replace(/[\\]/g, '');
        const value = match[1];
        const unit = match[2] || '%';
        
        metrics.push({
          metric_name: metricName,
          current_value: `${value}${unit}`,
          target_value: this.getTargetForMetric(metricName),
          status: this.determineMetricStatus(metricName, parseFloat(value)),
          trend: 'stable'
        });
      }
    });

    return metrics;
  }

  private getTargetForMetric(metricName: string): string {
    const targets: { [key: string]: string } = {
      'Database Response Time': '< 100ms',
      'Query Success Rate': '99.5%',
      'CPU Utilization': '< 70%',
      'Memory Utilization': '< 80%',
      'Storage': '< 85%',
      'Index Fragmentation': '< 10%'
    };
    return targets[metricName] || '';
  }

  private determineMetricStatus(metricName: string, value: number): string {
    const thresholds: { [key: string]: { good: number; warning: number } } = {
      'Database Response Time': { good: 100, warning: 200 },
      'Query Success Rate': { good: 99, warning: 95 },
      'CPU Utilization': { good: 70, warning: 85 },
      'Memory Utilization': { good: 80, warning: 90 },
      'Storage': { good: 85, warning: 95 },
      'Index Fragmentation': { good: 10, warning: 20 }
    };

    const threshold = thresholds[metricName];
    if (!threshold) return 'on-track';

    if (metricName === 'Query Success Rate') {
      return value >= threshold.good ? 'on-track' : value >= threshold.warning ? 'at-risk' : 'behind';
    } else {
      return value <= threshold.good ? 'on-track' : value <= threshold.warning ? 'at-risk' : 'behind';
    }
  }

  private extractReportSummary(content: string): string {
    const summaryMatch = content.match(/REPORT SUMMARY\s*([^|]*?)(?=\||\n\n|WORK ACCOMPLISHED)/i);
    return summaryMatch?.[1]?.trim() || '';
  }

  private extractConclusions(section: string): string {
    return section.replace(/CONCLUSIONS[\s\S]*?RECOMMENDATIONS/i, '').trim();
  }

  private getFileTypeFromExtension(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'application/pdf';
      case 'md': return 'text/markdown';
      case 'txt': return 'text/plain';
      default: return 'application/octet-stream';
    }
  }
}

export default WSRUploadService;